import { useEffect, useRef, useState } from "react";
// Import the same CSS used by all widgets
import cssText from "~/widgets/styles/globals.css?inline";

/**
 * Own document.body container + open shadow root + globals.css (with :root
 * rewritten to :host) + a mount point — what TermsDialogPortal does inline
 * (see TermsDialogPortal.tsx:360-396, 506). Dialogs must live outside the
 * element's shadow root (fixed positioning, stacking) but still get our
 * styles and the school's --primary.
 */
export function useShadowPortal(args: { open: boolean; id: string; primaryColor?: string }) {
  const { open, id, primaryColor = "#000000" } = args;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [mount, setMount] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    // Create container
    const container = document.createElement("div");
    container.id = id;
    container.style.cssText = "position: fixed; inset: 0; z-index: 99999; pointer-events: auto;";
    document.body.appendChild(container);
    containerRef.current = container;

    // Create shadow root
    const shadowRoot = container.attachShadow({ mode: "open" });

    // Inject CSS with :root replaced by :host
    const style = document.createElement("style");
    const processedCss = cssText.replace(/:root/g, ":host");
    style.textContent = processedCss + `
      :host {
        --primary: ${primaryColor};
      }
    `;
    shadowRoot.appendChild(style);

    // Create mount point for React content
    const mountPoint = document.createElement("div");
    mountPoint.className = `${id}-mount`;
    shadowRoot.appendChild(mountPoint);
    setMount(mountPoint);

    return () => {
      container.remove();
      containerRef.current = null;
      setMount(null);
    };
  }, [open, id, primaryColor]);

  return mount;
}
